<template>
  <div
      class="app-radio-card-buttons"
      :class="{
        invalid: hasError,
        multiple,
        toggle,
      }"
  >
    <div
        v-if="hasSlotDefault"
        class="app-radio-card-buttons__label"
    >
      <div>
        <slot></slot>
      </div>
      <div
          v-if="hasSlotTooltip"
          class="app-radio-card__tooltip"
      >
        <Tooltip>
          <FontAwesomeIcon :con="faInfoCircle"/>
          <template #title><slot name="tooltip"></slot></template>
        </Tooltip>
      </div>
    </div>
    <div
        class="app-radio-card-buttons__options"
        :style="{
          '--options-grid-template': currentGridTemplate,
        }"
    >
      <template
          v-for="(option, inx) in options"
          :key="`option-${inx}-${option.value}`"
      >
        <div
            class="option"
            :class="{
              'option': true,
              'selected': getOptionIsSelected(option),
              'disabled': getOptionIsDisabled(option),
            }"
            @click="onSelectOption(option)"
        >
          <slot name="option" v-bind="{
              option,
              multiple,
              selected: getOptionIsSelected(option),
              disabled: getOptionIsDisabled(option),
          }">
            <div class="option-inner--default">
              <div
                  class="option__label"
                  v-html="option.label"
              />
              <div
                  v-if="option.description"
                  class="option__description"
              >
                <span v-html="option.description" />
                <Tooltip v-if="option.hint">
                  <FontAwesomeIcon :icon="faInfoCircle"/>
                  <template #title><span v-html="option.hint"></span></template>
                </Tooltip>
              </div>
            </div>
          </slot>
        </div>
      </template>
    </div>
    <div
        v-if="hasSlotHint"
        class="app-radio-card-buttons__hint"
    >
      <slot name="hint"></slot>
    </div>
    <div
        v-if="hasError"
        class="app-radio-card-buttons__error"
        v-html="errorMessage"
    />
  </div>
</template>

<script lang="ts" setup>
import Tooltip from '@/components/Tooltip.vue';
import {
  onBreakpointChanged,
  removeOnBreakpointChanged,
} from '@/utils/viewport';
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  useSlots,
} from 'vue';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

library.add(faInfoCircle);

const modelValue = defineModel();

interface OptionInterface {
  label: string
  value: any
  description?: string
  fixed?: boolean
  excludes?: any[]
  hint?: string
}

const props = withDefaults(defineProps<{
  error?: string | string[]
  options?: OptionInterface[]
  multiple?: boolean
  toggle?: boolean
  atLeastOneOf?: string[]
  switchExcludes?: boolean
  gridTemplate?: string | Record<string, string>,
}>(), {
  options: () => ([
    { label: 'Ja', value: true },
    { label: 'Nein', value: false },
  ]),
  gridTemplate: () => ({
    xs: '1fr 1fr',
    lg: '1fr 1fr 1fr',
  }),
});

const hasError = computed(() => (props.error ? props.error.length > 0 : false));

const errorMessage = computed(() => {
  if (hasError.value) {
    return Array.isArray(props.error)
      ? props.error[0]
      : props.error;
  }

  return '';
});

const getOptionIsSelected = (option: OptionInterface) => {
  if (option.fixed) return true;

  if (props.multiple && Array.isArray(modelValue.value)) return modelValue.value.includes(option.value);

  return modelValue.value === option.value;
};

const isDisabledByExcludes = (option: OptionInterface) => {
  const disabledByExcludes = [...(Array.isArray(modelValue.value) ? modelValue.value : [])]
    .map((value) => props.options.find((o) => o.value === value && o.value !== option.value)?.excludes || [])
    .flat();

  return disabledByExcludes.includes(option.value);
};
const getOptionIsDisabled = (option: OptionInterface) => {
  if (getOptionIsSelected(option)) return false;
  if (!props.multiple) return modelValue.value !== undefined ? modelValue.value !== option.value : false;

  return isDisabledByExcludes(option);
};

const onSelectOption = (option: OptionInterface) => {
  if (option.fixed) return;

  if (!props.multiple) {
    if (props.toggle && modelValue.value === option.value) {
      modelValue.value = undefined;

      return;
    }

    modelValue.value = option.value;

    return;
  }

  if (!props.switchExcludes && getOptionIsDisabled(option)) return;

  const currentVal: any[] = [...(Array.isArray(modelValue.value) ? modelValue.value : [])];

  const excludes = option.excludes || [];
  if (excludes.length) {
    excludes.forEach((exclude) => {
      const indexToRemove = currentVal.findIndex((val) => val === exclude);

      if (indexToRemove >= 0) {
        currentVal.splice(indexToRemove, 1);
      }
    });
  }

  if (currentVal.includes(option.value)) {
    const indexToRemove = currentVal.findIndex((val) => val === option.value);
    if (indexToRemove < 0) return;

    currentVal.splice(indexToRemove, 1);
  } else {
    currentVal.push(option.value);
  }

  if (!Array.isArray(props.atLeastOneOf)) {
    modelValue.value = currentVal;

    return;
  }

  const atLeast = props.atLeastOneOf.filter((oneOf) => currentVal.includes(oneOf)).length > 0;

  if (!atLeast) return;

  modelValue.value = currentVal;
};

const slots = useSlots();
const slotDefault = slots.default ? slots.default() : null;
const hasSlotDefault = computed(() => (
  slotDefault && slotDefault[0] ? `${slotDefault[0].children}`.trim().length > 0 : false
));
const slotTooltip = slots.tooltip ? slots.tooltip() : null;
const hasSlotTooltip = computed(() => (
  slotTooltip && slotTooltip[0] ? `${slotTooltip[0].children}`.trim().length > 0 : false
));
const slotHint = slots.hint ? slots.hint() : null;
const hasSlotHint = computed(() => (
  slotHint && slotHint[0] ? `${slotHint[0].children}`.trim().length > 0 : false
));

const currentGridTemplate = ref<string>();
const getCurrentGridTemplate = (event: CustomEvent) => {
  let { gridTemplate } = props;
  const breakpoints = (event.detail?.breakpoints || ['xs']) as string[];

  if (typeof gridTemplate === 'object') {
    let sizeByBp = '1fr';
    const propBps = gridTemplate;

    breakpoints.forEach((bpKey) => {
      if (Object.keys(gridTemplate).includes(bpKey)) {
        sizeByBp = `${propBps[bpKey] || gridTemplate || '1fr'}`.trim();
      }
    });

    gridTemplate = sizeByBp || '1fr';
  }

  currentGridTemplate.value = `${gridTemplate || '1fr'}`.trim();
};

onBeforeMount(() => {
  onBreakpointChanged(getCurrentGridTemplate, true);
});

onBeforeUnmount(() => {
  removeOnBreakpointChanged(getCurrentGridTemplate);
});
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.app-radio-card-buttons {
  display: grid;
  gap: 10px;
  grid-template-columns: 100%;
  margin-bottom: 10px;

  @media (min-width: map_get($grid-breakpoints, 'lg')) {
    margin-bottom: 20px;
    gap: 20px;
  }

  &__label, .option__label, .option__description {
    font-size: .875rem;
    font-weight: 300;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      font-size: 1.125rem;
    }
  }

  .option__label {
    font-weight: 700;
  }

  &__label {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr auto;

    > div:last-child {
      font-size: 1.11111em;
      padding-left: 10px;
    }
  }

  &__options {
    display: grid;
    gap: 10px;
    grid-template-columns: var(--options-grid-template, 1fr 1fr 1fr);

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      gap: 20px;
    }

    > .option .option-inner--default {
      display: grid;
      height: 100%;
      gap: 5px;
      cursor: pointer;
      background-color: #fff;
      border-radius: 10px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
      border: 2px solid #fff;
      padding: 10px;
      justify-content: center;
      grid-column: auto;
      text-align: center;
      position: relative;
      overflow: hidden;
      grid-template-rows: 1fr auto auto 1fr;
      align-items: center;

      > div {
        &:first-child {
          grid-row: 2 / 3;
        }

        &:last-child {
          grid-row: 3 / 4;
        }
      }

      &:after {
        content: '';
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, .7);
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
        transition: opacity .075s ease-in-out;
        will-change: opacity;
      }
    }

    > .option.selected .option-inner--default {
      cursor: default;
      border-color: #2B257E;
    }

    > .option.disabled .option-inner--default {
      &:after {
        opacity: 1;
      }

      &:hover:after {
        opacity: 0;
      }
    }
  }

  &__hint {
    background-color: #FFFFFF;
    border-radius: 10px;
    padding: 20px;
  }

  &.multiple, &.toggle {
    .option.selected {
      cursor: pointer;
    }
  }
}
</style>
